import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader, RetryState } from "@/components/widgets";
import { AssetLogo } from "@/components/AssetLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, ArrowClockwise, Check, X, Target, ArrowDown, TrendUp, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const scoreColor = (s) => s >= 80 ? "text-signal-green" : s >= 60 ? "text-signal-yellow" : "text-muted-foreground";
const fmt = (p) => p >= 1 ? `$${p.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : `$${p}`;

export default function Ideas() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [broker, setBroker] = useState({ linked: false, paper: true });
  const [dismissed, setDismissed] = useState({});
  const [accepted, setAccepted] = useState({});
  const [ov, setOv] = useState({}); // per-ticker {qty, route}
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setErr(false); setData(null);
    api.get("/ideas").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/alpaca/status").then((r) => setBroker(r.data)).catch(() => {}); }, []);

  // Cold cache: the backend returns status "generating" and builds ideas in the
  // background. Poll every 5s until they're ready (no long-blocking request).
  useEffect(() => {
    if (data?.status !== "generating") return;
    const id = setInterval(() => {
      api.get("/ideas").then((r) => setData(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [data?.status]);

  const setField = (tk, k, v) => setOv((o) => ({ ...o, [tk]: { ...o[tk], [k]: v } }));

  const accept = async (idea) => {
    const o = ov[idea.ticker] || {};
    const qty = Number(o.qty ?? idea.suggested_qty);
    const route = o.route || "paper";
    if (!(qty > 0)) { toast.error("Enter a valid quantity"); return; }
    if (route === "broker" && !broker.linked) { toast.error("Link your Alpaca account first (Trading → Alpaca Broker)"); return; }
    if (route === "broker" && !broker.paper && !window.confirm(`Place a REAL ${idea.ticker} order for ${qty}? Uses real money.`)) return;
    setBusy(idea.ticker);
    try {
      const r = await api.post("/ideas/accept", { ticker: idea.ticker, side: "buy", quantity: qty, route, live_confirmed: route === "broker" && !broker.paper });
      toast.success(`Accepted · ${idea.ticker} ${route === "broker" ? "→ Alpaca" : "paper"} ${r.data.order?.status || "filled"}`);
      setAccepted((a) => ({ ...a, [idea.ticker]: true }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data || data.status === "generating") return err
    ? <RetryState label="Couldn't generate ideas — the market data or AI feed may be busy." onRetry={load} />
    : <Loader label="AI is scanning the market for setups…" />;

  const visible = data.ideas.filter((i) => !dismissed[i.ticker]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-xs text-volt">Intelligence Layer</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><Sparkle size={24} weight="fill" className="text-volt" /> AI Trade Ideas</h1>
        </div>
        <Button data-testid="refresh-ideas-btn" onClick={load} variant="outline" className="rounded-sm border-border gap-2 h-9"><ArrowClockwise size={15} /> Refresh</Button>
      </div>

      <div className="mt-4 flex items-start gap-2 border border-signal-yellow/30 bg-signal-yellow/[0.05] p-3 rounded-sm">
        <WarningCircle size={16} className="text-signal-yellow shrink-0 mt-0.5" weight="fill" />
        <p className="text-xs text-muted-foreground">These are <b className="text-signal-yellow">probabilistic model outputs, not financial advice.</b> Each idea is ranked by MarketPilot's AI score & forecast; sizing suggests ~5% of your paper equity (${(data.equity || 0).toLocaleString()}). Review before accepting.</p>
      </div>

      {visible.length === 0 ? (
        <div className="mt-10 text-center text-sm text-muted-foreground">No open ideas right now — hit Refresh to re-scan the market.</div>
      ) : (
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {visible.map((idea) => {
            const o = ov[idea.ticker] || {};
            const route = o.route || "paper";
            const done = accepted[idea.ticker];
            return (
              <div key={idea.ticker} data-testid={`idea-card-${idea.ticker}`} className="border border-border bg-card rounded-sm overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border/60">
                  <button onClick={() => nav(`/stock/${idea.ticker}`)} className="flex items-center gap-2.5 text-left">
                    <AssetLogo ticker={idea.ticker} size={30} />
                    <div>
                      <div className="mono font-semibold flex items-center gap-2">{idea.ticker.replace("-USD", "")}
                        <span className="mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{idea.asset}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[160px]">{idea.name}</div>
                    </div>
                  </button>
                  <div className="text-right">
                    <div className={`mono text-xl font-bold ${scoreColor(idea.score)}`}>{idea.score}</div>
                    <div className="label-xs">AI Score</div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <p className="text-sm text-muted-foreground leading-relaxed">{idea.thesis}</p>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="border border-border/60 rounded-sm py-2"><div className="label-xs flex items-center justify-center gap-1"><TrendUp size={11} className="text-volt" />Entry</div><div className="mono text-sm mt-0.5">{fmt(idea.entry)}</div></div>
                    <div className="border border-signal-green/30 bg-signal-green/[0.04] rounded-sm py-2"><div className="label-xs flex items-center justify-center gap-1"><Target size={11} className="text-signal-green" />Target</div><div className="mono text-sm mt-0.5 pos">{fmt(idea.target)}</div></div>
                    <div className="border border-signal-red/30 bg-signal-red/[0.04] rounded-sm py-2"><div className="label-xs flex items-center justify-center gap-1"><ArrowDown size={11} className="text-signal-red" />Stop</div><div className="mono text-sm mt-0.5 neg">{fmt(idea.stop)}</div></div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {idea.factors?.map((f, i) => <span key={i} className="text-[10px] mono px-1.5 py-0.5 rounded-sm bg-signal-green/10 text-signal-green border border-signal-green/20">{f}</span>)}
                    {idea.risks?.map((f, i) => <span key={`r${i}`} className="text-[10px] mono px-1.5 py-0.5 rounded-sm bg-signal-red/10 text-signal-red border border-signal-red/20">⚠ {f}</span>)}
                  </div>

                  {done ? (
                    <div className="flex items-center gap-2 text-signal-green text-sm py-2" data-testid={`idea-accepted-${idea.ticker}`}><Check size={16} weight="bold" /> Trade placed — view it in your {route === "broker" ? "Broker" : "Paper"} account.</div>
                  ) : (
                    <div className="flex items-center gap-2 pt-1">
                      <div className="flex-1">
                        <div className="label-xs mb-1">Quantity</div>
                        <Input data-testid={`idea-qty-${idea.ticker}`} type="number" step="any" value={o.qty ?? idea.suggested_qty}
                          onChange={(e) => setField(idea.ticker, "qty", e.target.value)} className="bg-background rounded-sm mono h-9" />
                      </div>
                      <div className="w-[120px]">
                        <div className="label-xs mb-1">Route</div>
                        <Select value={route} onValueChange={(v) => setField(idea.ticker, "route", v)}>
                          <SelectTrigger data-testid={`idea-route-${idea.ticker}`} className="bg-background rounded-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paper">Paper</SelectItem>
                            <SelectItem value="broker">Alpaca{broker.linked ? (broker.paper ? " (paper)" : " (LIVE)") : " (link)"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 self-end">
                        <Button data-testid={`idea-accept-${idea.ticker}`} disabled={busy === idea.ticker} onClick={() => accept(idea)} className="bg-signal-green hover:bg-signal-green/90 text-black rounded-sm h-9 gap-1"><Check size={15} weight="bold" /> Accept</Button>
                        <Button data-testid={`idea-deny-${idea.ticker}`} variant="outline" onClick={() => setDismissed((d) => ({ ...d, [idea.ticker]: true }))} className="rounded-sm border-border h-9 gap-1"><X size={15} /> Deny</Button>
                      </div>
                    </div>
                  )}
                  <div className="mono text-[10px] text-muted-foreground">Suggested ≈ {idea.suggested_qty} units (${idea.alloc_value?.toLocaleString()}) · horizon {idea.horizon} · confidence {Math.round((idea.confidence || 0) * 100)}%</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
